import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { COUNTRIES, getCountryName } from '@/data/countries';

interface CountryTagPickerProps {
  /** Currently selected ISO 3166-1 alpha-2 codes. */
  value: string[];
  /** Called with the new selection on every add/remove. */
  onChange: (codes: string[]) => void;
  /** Disable interactions (e.g. while saving). */
  disabled?: boolean;
  /** Placeholder shown on the "Add country" button. */
  addLabel?: string;
  /** Placeholder shown in the search input. */
  searchPlaceholder?: string;
  /** Empty-state copy when no chip is selected. */
  emptyLabel?: string;
}

export const CountryTagPicker: React.FC<CountryTagPickerProps> = ({
  value,
  onChange,
  disabled = false,
  addLabel = 'Add a country',
  searchPlaceholder = 'Search country...',
  emptyLabel = 'No country selected yet',
}) => {
  const [open, setOpen] = useState(false);

  const normalizedValue = useMemo(
    () => value.map((c) => c.toUpperCase()).filter(Boolean),
    [value]
  );

  const availableCountries = useMemo(
    () => COUNTRIES.filter((c) => !normalizedValue.includes(c.code)),
    [normalizedValue]
  );

  const handleAdd = (code: string) => {
    if (normalizedValue.includes(code)) return;
    onChange([...normalizedValue, code]);
    setOpen(false);
  };

  const handleRemove = (code: string) => {
    onChange(normalizedValue.filter((c) => c !== code));
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {normalizedValue.length === 0 && (
        <span className="text-sm text-[#98a2b3]" data-testid="country-picker-empty">
          {emptyLabel}
        </span>
      )}
      {normalizedValue.map((code) => (
        <span
          key={code}
          data-testid={`country-chip-${code}`}
          className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1 rounded-full bg-[#be43ff]/10 text-[#be43ff] text-sm border border-[#be43ff]/20"
        >
          <span>{getCountryName(code)}</span>
          <span className="text-[#be43ff]/70 text-xs">{code}</span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleRemove(code)}
            aria-label={`Remove ${getCountryName(code)}`}
            className="w-5 h-5 rounded-full hover:bg-[#be43ff]/20 inline-flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}

      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid="country-picker-add"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-dashed border-gray-300 text-sm text-[#475467] hover:border-[#be43ff] hover:text-[#be43ff] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            {addLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              {availableCountries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => handleAdd(country.code)}
                >
                  <span className="flex-1">{country.name}</span>
                  <span className="text-xs text-[#98a2b3]">{country.code}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
